import { useState, useEffect, useRef } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";
import {
  ArrowsRepeat,
  CircleExclamation,
  PaperPlane,
  Pen
} from "@unified-checkout/design-system/icons/index";
import {
  Modal,
  Input,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  PhoneInput,
  Notification,
  SkeletonBase,
  Label
} from "@unified-checkout/design-system";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { COUNTRIES_INFO } from "@app/dashboard/constants/countries";
import { SYSTEM_EVENTS_GROUPING_CODES } from "@app/dashboard/constants/tracking_events/system";
import { useLangStore } from "@app/dashboard/store/lang";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { changeRole } from "@app/dashboard/services/inner_layout/user_management/users/change-role";
import { inviteMember } from "@app/dashboard/services/inner_layout/user_management/users/invite-member";
import type {
  IUserRoleModal
} from "@app/dashboard/types/innerLayout/user_management/modals/user-role";
import type { IField } from "@app/dashboard/types/outerLayout/common";
import type {
  IRoleManagementLocationProps
} from "@app/dashboard/types/innerLayout/user_management";
import {
  emailValidation,
  isFormValid,
  isPhoneNumber,
  maxXChars,
  phoneNumberValidation,
  required,
  validateField
} from "@app/dashboard/utils/formValidations";
import {
  getCountryFromDomain
} from "@app/dashboard/utils/getCountryFromDomain";
import { iso3ToIso2 } from "@app/dashboard/utils/countriesUtils";
import { notify } from "@app/dashboard/utils/notifier";
import { getToasterConfig, MAX_EMAIL_LENGTH } from "../utils";
import useGetRolesList from "../../../useGetRolesList";
import styles from "./user-role-modal.module.scss";

const FIELDS_NAMES = {
  EMAIL_OR_PHONE_NUMBER: 'change-role-modal-email-or-phone-number',
  ROLE: 'change-role-modal-role',
};

export const UserRoleModal = ({
  type,
  open = false,
  setOpen,
  isMobile = false,
  id: userId,
  userName,
  onSuccessHandler,
  onErrorHandler
}:
  IUserRoleModal
) => {
  const changeRoleOnlyType = type === 'changeRole' || type === 'updateExistingRole';
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const country = getCountryFromDomain();
  const countryIso2 = iso3ToIso2(country);

  const isPhone = isPhoneNumber(userName || '');
  const isEmail = emailValidation.pattern.test(userName || '');
  const isOldUserNameFormat = changeRoleOnlyType && (!isPhone && !isEmail);

  const [shouldRenderPhoneInput, setShouldRenderPhoneInput] = useState(isPhone && !isEmail);
  const [focusedElement, setFocusedElement] = useState<string | undefined>();

  const {
    roles,
    isLoading: isRolesResponseLoading,
    isError: isRolesResponseError,
    isRefetching: isRolesResponseRefetching
  } = useGetRolesList({ type: type, id: userId, enabled: open });

  const { label: roleLabel, id: roleId } = roles?.find(item => item.selected) || {};
  const currentRoleRef = useRef<string>();

  const [currentRole, setCurrentRole] = useState({
    label: roleLabel,
    id: roleId
  });

  const phoneNumberValidations = [
    required,
    ...(isOldUserNameFormat ? [] : [phoneNumberValidation(country)])
  ];

  const emailValidations = [
    required,
    ...(isOldUserNameFormat ? [] : [emailValidation, maxXChars(MAX_EMAIL_LENGTH)])
  ];

  const fieldsInitialState = {
    [FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER]: {
      value: userName || '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    },
    [FIELDS_NAMES.ROLE]: {
      value: roleLabel || '',
      isValid: undefined,
      errorMsg: '',
      validations: [required]
    }
  };
  const [fields, setFields] = useState<IField>(fieldsInitialState);

  const resetDefaults = () => {
    setCurrentRole({ label: roleLabel, id: roleId });
    setFields(fieldsInitialState);
    currentRoleRef.current = roleLabel;
  };

  useEffect(() => {
    const { label: updatedLabel, id: updatedRoleId } = roles?.find(item => item.selected) || {};
    setCurrentRole({ label: updatedLabel, id: updatedRoleId });
    currentRoleRef.current = updatedLabel;
  }, [roles]);

  useEffect(() => {
    if (open) {
      resetDefaults();

      setShouldRenderPhoneInput(isPhone && !isEmail);

      const fieldsToValidate = [
        { value: userName, field: FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER },
        { value: currentRole.label, field: FIELDS_NAMES.ROLE }
      ];

      fieldsToValidate.forEach(({ value, field }) =>
        (typeof value === 'string' || typeof value === 'number')
        && validateField(fields, setFields, field, value)
      );

    } else {
      resetDefaults();
    }
  }, [open]);

  useEffect(() => {
    const emailOrPhoneNumber = fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value;
    const role = fields[FIELDS_NAMES.ROLE].value;

    typeof emailOrPhoneNumber === 'string' && validateField(fields, setFields, FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER, emailOrPhoneNumber);
    typeof role === 'string' && validateField(fields, setFields, FIELDS_NAMES.ROLE, role);
  }, [lang]);

  useEffect(() => {
    const emailOrPhone = fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER];

    emailOrPhone.isValid !== undefined &&
      validateField(fields, setFields, FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER, emailOrPhone.value as string);
  }, [fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].validations]);

  const TEXT: Record<IUserRoleModal['type'], Record<string, string>> = {
    changeRole: {
      modalTitle: t('innerLayout.userManagementPage.modals.changeRoleModal.title'),
      modalSupportingText: t('innerLayout.userManagementPage.modals.changeRoleModal.supportingText'),
      primaryBtnText: t('form.save'),
      secondaryBtnText: t('form.cancel'),
      toasterText: t('innerLayout.userManagementPage.modals.changeRoleModal.toasterText'),
      toasterSupportingText: t('innerLayout.userManagementPage.modals.changeRoleModal.toasterSuppText')
        .replace('{{email_or_phone}}', fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value),
    },
    inviteMember: {
      modalTitle: t('innerLayout.userManagementPage.modals.inviteMemberModal.title'),
      modalSupportingText: t('innerLayout.userManagementPage.modals.inviteMemberModal.supportingText'),
      primaryBtnText: t('innerLayout.userManagementPage.modals.inviteMemberModal.primaryBtnText'),
      secondaryBtnText: t('form.cancel'),
      toasterText: t('innerLayout.userManagementPage.modals.inviteMemberModal.toasterText'),
      toasterSupportingText: t('innerLayout.userManagementPage.modals.inviteMemberModal.toasterSuppText'),
    },
    updateExistingRole: {
      modalTitle: t('innerLayout.userManagementPage.modals.updateExistingRoleModal.title'),
      modalSupportingText: t('innerLayout.userManagementPage.modals.updateExistingRoleModal.supportingText'),
      primaryBtnText: t('innerLayout.userManagementPage.modals.updateExistingRoleModal.primaryBtnText'),
      secondaryBtnText: t('form.cancel'),
      toasterText: t('innerLayout.userManagementPage.modals.updateExistingRoleModal.toasterText'),
      toasterSupportingText: t('innerLayout.userManagementPage.modals.updateExistingRoleModal.toasterSuppText')
        .replace('{{email_or_phone}}', fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value),
      selectRoleHint: t('innerLayout.userManagementPage.modals.updateExistingRoleModal.selectRoleHint'),
    }
  };


  const mutation_key = [
    type === 'changeRole' ?
      QUERY_KEYS.CHANGE_ROLE
      :
      type === 'updateExistingRole' ?
        QUERY_KEYS.UPDATE_EXISTING_ROLE
        :
        QUERY_KEYS.INVITE_MEMBER
  ];

  // Annotated because the two branches post different bodies: axios infers the
  // request-data type into AxiosResponse, so without this the union of the two
  // response types isn't assignable to a single useMutation TData.
  const mutationFunction = (): Promise<AxiosResponse> => {
    const userName = fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value || '';

    return changeRoleOnlyType ?
      changeRole({ id: userId as number, roleId: currentRole.id as string })
      :
      inviteMember({
        emailOrPhone: userName,
        roleId: currentRole.id as string
      });
  };

  const trackingCode = changeRoleOnlyType ?
    SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.CHANGE_ROLE
    :
    SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.INVITE_MEMBER;

  const userIdentifier = fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value || '';
  const isInvitedByEmail = userIdentifier.includes('@');

  const {
    isPending,
    mutate
  } = useMutation({
    mutationKey: mutation_key,
    mutationFn: mutationFunction,
    onSuccess: () => {
      setOpen?.(false);

      onSuccessHandler?.({
        userId,
        fromRole: currentRoleRef.current,
        role: currentRole.label,
        invitedBy: isInvitedByEmail ? 'email' : 'phone'
      });

      queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_USERS] });
      notify.success(
        TEXT[type].toasterText,
        getToasterConfig({
          type: 'success',
          msg: TEXT[type].toasterText,
          trackingCode,
          extra: {
            hintText: TEXT[type].toasterSupportingText,
          },
        })
      );
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message || t('errors.somethingWentWrong');

      onErrorHandler?.({
        err: msg,
        role: currentRole.label,
        invitedBy: isInvitedByEmail ? 'email' : 'phone'
      });

      setOpen?.(false);
      notify.error(
        msg,
        getToasterConfig({
          type: 'error',
          msg,
          trackingCode,
        })
      );
    },
  });

  const onChangeHandler = (fieldName: string, value: string) => {

    const isEmail = emailValidation.pattern.test(value || '');
    const isPhone = isPhoneNumber(value || '');
    fieldName === FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER && setShouldRenderPhoneInput(isPhone && !isEmail);
    setFocusedElement(fieldName === FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER ? FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER : FIELDS_NAMES.ROLE);

    setFields(prevState => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        value: value,
        validations:
          fieldName === FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER ?
            (
              isPhone ?
                phoneNumberValidations
                :
                emailValidations
            )
            :
            [required]
      }
    }));

    validateField(fields, setFields, fieldName, value);
  };

  const handleRoleChange = (role: string, roleId: string) => {
    setCurrentRole({ label: role, id: roleId });
    onChangeHandler(FIELDS_NAMES.ROLE, role);
  };

  const handlePhoneChange = (number: string) => {
    const numWithoutPhoneCountryCode = number.replace(COUNTRIES_INFO[country].phone_country_code, '');

    onChangeHandler(FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER, numWithoutPhoneCountryCode);
  };

  const handleEmailChange = (value: string) => onChangeHandler(FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER, value);


  const handleCancel = () => setOpen?.(false);

  const handleSave = () => mutate();


  const formValid = isFormValid(fields);

  const modalHeaderIcon =
    type === 'changeRole' ?
      Pen
      :
      type === 'updateExistingRole' ?
        ArrowsRepeat
        :
        PaperPlane;


  const clickHereHandler = (to: 'manage-roles' | 'role-details') => {
    const user_id = (userId || '')?.toString();
    if (to === 'manage-roles') {
      navigate(INNER_ROUTES.MANAGE_ROLES)
    } else {
      const roleDetailsPath = generatePath(INNER_ROUTES.ROLE_DETAILS, { id: roleId || user_id });
      navigate(roleDetailsPath, {
        state: {
          pageType: 'role_details',
          userId: user_id,
          preventEdit: true
        } as IRoleManagementLocationProps,
      });
    };
  };

  const replaceTextWithLink = (text: string, route: 'manage-roles' | 'role-details') => {
    const parts = text.split(/\[\[(.+?)\]\]/g);

    const jsx = parts.map((part, i) =>
      i % 2 === 1 ?
        (
          <span
            key={`user-role-modal-supp-text-part-${i}`}
            className={styles["user-role-modal__click-here-link"]}
            onClick={() => clickHereHandler(route)}
          >
            {part}
          </span>
        )
        : <span key={`user-role-modal-supp-text-part-${i}`}>{part}</span>
    );

    return <>{jsx}</>;
  };

  const inputFieldLabel =
    (country === 'EGY' && type !== 'inviteMember') ?
      t('innerLayout.userManagementPage.form.emailOrPhoneNumberOrUsername')
      :
      t('form.emailOrPhoneNumber');

  return (
    <>
      <div className={styles["user-role-modal"]}>
        <Modal
          state="default"
          isMobile={isMobile}
          open={open}
          closeOnClickOutside={false}
          headerIcon={modalHeaderIcon}
          title={TEXT[type].modalTitle}
          supportingText={replaceTextWithLink(TEXT[type].modalSupportingText, 'role-details') as any}
          btnsProps={[
            {
              size: "lg",
              color: "primary",
              variant: "contained",
              text: TEXT[type].primaryBtnText,
              loading: isPending,
              disabled: (!formValid || isPending),
              onClickHandler: handleSave
            },
            {
              size: "lg",
              color: "secondary",
              variant: "outlined",
              text: TEXT[type].secondaryBtnText,
              onClickHandler: handleCancel
            }
          ]}
        >
          <div className={styles["user-role-modal__body"]}>

            {
              shouldRenderPhoneInput ?
                <PhoneInput
                  id={FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER}
                  name={FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER}
                  label={inputFieldLabel}
                  disableSelectCountry={true}
                  autoFocus={focusedElement === FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER}
                  phoneNumber={fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value}
                  onChangeHandler={handlePhoneChange}
                  country={countryIso2}
                  error={fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].errorMsg}
                  disabled={changeRoleOnlyType}
                  canToggleToTextInput={true}
                />
                :
                <Input
                  id={FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER}
                  name={FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER}
                  type='text'
                  label={inputFieldLabel}
                  value={fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].value}
                  required
                  autoFocus={focusedElement === FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  status={fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].isValid === false ? 'error' : 'default'}
                  error={fields[FIELDS_NAMES.EMAIL_OR_PHONE_NUMBER].errorMsg}
                  disabled={changeRoleOnlyType}
                />
            }

            {
              isRolesResponseLoading || isRolesResponseRefetching ?
                <div className={styles["user-role-modal__body__roles-list-loader"]}>
                  <Label text={t('innerLayout.userManagementPage.form.Role')} />
                  <SkeletonBase height="2.5rem" />
                </div>
                :
                <Dropdown
                  label={t('innerLayout.userManagementPage.form.Role')}
                  hintText={replaceTextWithLink(TEXT[type].selectRoleHint || '', 'manage-roles') as any}
                >
                  <DropdownButton
                    type="input"
                    leadingIcon={type === 'updateExistingRole' ? CircleExclamation : undefined}
                    placeholder={t('innerLayout.userManagementPage.form.selectRole')}
                    text={currentRole.label}
                  />

                  <DropdownMenu
                    maxHeight="12rem"
                    verticalPlacement="auto"
                  >
                    {
                      roles?.map((role, i) =>
                        <DropdownMenuItem
                          key={`change-role-${role.id}-${i}`}
                          text={role.label}
                          hintText={role.description}
                          selected={currentRole.id === role.id || role.selected}
                          onClickHandler={() => handleRoleChange(role.label, role.id)}
                        />
                      )
                    }
                  </DropdownMenu>
                </Dropdown>
            }

          </div>
        </Modal>

      </div >

    </>
  )
}
